import React, { useEffect, useRef, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Autocomplete,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
} from '@mui/material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { Add } from '@mui/icons-material';

interface Reseller {
    id: number;
    resellerName: string;
}

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
};

const ResellerAmountModal: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [resellers, setResellers] = useState<Reseller[]>([]);
    const [selectedResellers, setSelectedResellers] = useState<Reseller[]>([]);
    const [amountInput, setAmountInput] = useState('');
    const [coinType, setCoinType] = useState('BTC');
    const [remark, setRemark] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const fetchResellers = async () => {
        try {
            const response = await axios.get(`${API_PATH}/customer_ledger/allResellers`, getAxiosConfig());
            setResellers(response.data);
        } catch (err) {
            console.error('Failed to fetch resellers:', err);
        }
    };

    useEffect(() => {
        if (open) fetchResellers();
    }, [open]);

    const handleSave = async () => {
        if (!amountInput || isNaN(Number(amountInput)) || selectedResellers.length === 0) return;

        setSaveLoading(true);
        try {
            for (const reseller of selectedResellers) {
                const payload = {
                    resellerId: reseller.id,
                    amount: Number(amountInput),
                    coinType: coinType,
                    remark: remark || '',
                };

                await axios.post(`${API_PATH}/customer_ledger/credit`, payload, getAxiosConfig());
            }

            setOpen(false);
            setAmountInput('');
            setSelectedResellers([]);
            setRemark('');
        } catch (err) {
            console.error('Save error:', err);
        } finally {
            setSaveLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setAmountInput('');
        setSelectedResellers([]);
        setRemark('');
    };

    return (
        <>
            <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => setOpen(true)}
                startIcon={<Add />}
                sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    gap: 0,
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: 'info.dark',
                    },
                }}
            >
                Credit Reseller
            </Button>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="amount-modal-title"
                aria-describedby="amount-modal-description"
            >
                <Box sx={modalStyle}>
                    <Typography
                        id="amount-modal-title"
                        variant="subtitle1"
                        color="primary"
                        fontWeight={600}
                        mb={2}
                    >
                        Add Amount to Reseller
                    </Typography>

                    <Autocomplete
                        multiple
                        options={resellers}
                        getOptionLabel={(option) => option.resellerName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedResellers}
                        onChange={(_, val) => setSelectedResellers(val)}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Resellers" size="small" />
                        )}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        label="Amount"
                        type="text"
                        variant="outlined"
                        value={amountInput}
                        onChange={(e) => setAmountInput(e.target.value)}
                        sx={{ mb: 2 }}
                        inputProps={{
                            inputMode: 'decimal',
                            pattern: '[0-9]*',
                            'aria-describedby': 'amount-modal-description',
                        }}
                    />

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Coin Type</InputLabel>
                        <Select value={coinType} onChange={(e) => setCoinType(e.target.value)}>
                            <MenuItem value="BTC">BTC</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Remark"
                        variant="outlined"
                        size="small"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Enter remark for the transaction"
                        multiline
                        rows={2}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        sx={{ width: '100%', height: 40 }}
                        disabled={saveLoading || !amountInput || isNaN(Number(amountInput)) || selectedResellers.length === 0}
                    >
                        {saveLoading ? (
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                            'Add Amount'
                        )}
                    </Button>
                </Box>
            </Modal>
        </>
    );
};


export default ResellerAmountModal;
